import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { AlarmIcon, FireIcon } from "../lib/icons";
import { FadeUp } from "./Reveal";
import { EmSwash } from "./EmSwash";
import { PortraitFallback } from "./PortraitFallback";
import { CAMPAIGNS, IMAGES } from "../data/content";

const money = (n) => `$${Number(n).toLocaleString()}`;
const pad2 = (n) => String(n).padStart(2, "0");
const DAY_MS = 24 * 60 * 60 * 1000;

// Filter: less than or equal to 3 days left AND not fully funded.
const endingSoonCampaigns = (list) =>
  list
    .map((c, idx) => ({ ...c, _idx: idx }))
    .filter((c) => c.daysLeft <= 3 && c.raised < c.goal)
    .sort((a, b) => a.daysLeft - b.daysLeft);

const UrgencyDot = () => (
  <motion.span
    aria-hidden
    className="inline-block h-2 w-2 rounded-full bg-scarlet"
    animate={{ scale: [1, 1.4, 1], opacity: [1, 0.55, 1] }}
    transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
  />
);

// Compute how far each segment is from the deadline. `now` is the shared "current time"
// pushed from the parent's ticker so all cards stay in lock-step and only one interval
// runs for the whole section.
const partsFor = (deadline, now) => {
  const diff = Math.max(0, deadline - now);
  const days = Math.floor(diff / DAY_MS);
  const hours = Math.floor((diff / (60 * 60 * 1000)) % 24);
  const minutes = Math.floor((diff / (60 * 1000)) % 60);
  const seconds = Math.floor((diff / 1000) % 60);
  return { diff, days, hours, minutes, seconds };
};

// Live countdown pill — replaces the static "N days left" pill. Reuses the same
// pulsing scarlet ring animation for continuity. Digits use tabular-nums so the
// pill doesn't visibly shift width every second.
const CountdownPill = ({ deadline, now, testid }) => {
  const { diff, days, hours, minutes, seconds } = partsFor(deadline, now);
  const ended = diff === 0;
  const plur = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
  const aria = ended
    ? "Campaign ended"
    : `Time remaining: ${plur(days, "day")}, ${plur(hours, "hour")}, ${plur(minutes, "minute")}, ${plur(seconds, "second")}`;

  return (
    <motion.span
      role="timer"
      aria-live="off"
      aria-label={aria}
      data-testid={testid}
      className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-ink px-3 py-1.5 text-[13px] font-bold text-sand shadow-lg tabular-nums"
      animate={{ boxShadow: ["0 0 0 0 rgba(26,23,20,0.5)", "0 0 0 10px rgba(26,23,20,0)"] }}
      transition={{ duration: 1.8, repeat: Infinity, ease: "easeOut" }}
    >
      <AlarmIcon className="h-3.5 w-3.5" />
      {ended ? (
        <span className="uppercase tracking-wide">Ended</span>
      ) : (
        <span>
          {days}d {pad2(hours)}h {pad2(minutes)}m {pad2(seconds)}s
        </span>
      )}
    </motion.span>
  );
};

export const EndingSoon = () => {
  const rows = useMemo(() => endingSoonCampaigns(CAMPAIGNS), []);

  // Compute a stable deadline for each campaign once per mount so the tick is smooth
  // and doesn't jitter on state changes elsewhere.
  const deadlines = useMemo(() => {
    const start = Date.now();
    return rows.reduce((acc, c) => {
      acc[c._idx] = start + c.daysLeft * DAY_MS;
      return acc;
    }, {});
  }, [rows]);

  // Single shared ticker for every card in the section.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!rows.length) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [rows.length]);

  if (!rows.length) return null;

  return (
    <section
      id="ending-soon"
      data-testid="ending-soon-section"
      className="relative overflow-hidden border-y border-border bg-sand"
    >
      <span className="pointer-events-none absolute -right-24 top-1/2 h-96 w-96 -translate-y-1/2 rounded-full bg-grass/8 blur-3xl" />
      <span className="pointer-events-none absolute -left-24 bottom-0 h-72 w-72 rounded-full bg-tangerine/10 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-8 lg:py-28">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <FadeUp>
            <span className="inline-flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.24em] text-scarlet">
              <UrgencyDot /> Ending soon
            </span>
            <h2 className="mt-5 max-w-2xl font-serif text-3xl lg:text-[3.5rem] leading-[0.95] tracking-tight text-ink">
              Down to the wire. One nudge from the <EmSwash color="text-cobalt">finish line.</EmSwash>
            </h2>
          </FadeUp>
          <FadeUp delay={0.1}>
            <p className="max-w-sm text-[13px] text-olive">
              These students are close to their goal, and time is short. Your gift to My Rich Aunt could be the one that closes the gap.
            </p>
          </FadeUp>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {rows.map((c) => {
            const pct = Math.min(100, Math.round((c.raised / c.goal) * 100));
            const remaining = Math.max(0, c.goal - c.raised);
            return (
              <FadeUp key={c._idx} delay={0.05 * c._idx}>
                <Link
                  to={`/demo/campaign/${c._idx}`}
                  data-testid={`ending-soon-card-${c._idx}`}
                  className="group relative flex h-full flex-col overflow-hidden rounded-lg border border-border bg-bone transition-shadow hover:shadow-[0_18px_40px_-16px_rgba(10,155,6,0.35)]"
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    {IMAGES[c.img] ? (
                      <img
                        src={IMAGES[c.img]}
                        alt={c.name}
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    ) : (
                      <PortraitFallback
                        name={c.name}
                        school={c.school}
                        gradYear={c.gradYear}
                        variant={c.paletteVariant}
                        nameSize="text-[clamp(2.75rem,6.5vw,5rem)]"
                        className="transition-transform duration-700 group-hover:scale-105"
                      />
                    )}
                    <span
                      aria-hidden
                      className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/60 via-ink/10 to-transparent"
                    />
                    <CountdownPill
                      deadline={deadlines[c._idx]}
                      now={now}
                      testid={`ending-soon-countdown-${c._idx}`}
                    />
                    <div className="absolute inset-x-4 bottom-4 flex items-end justify-between text-sand">
                      <div>
                        <p className="font-serif text-3xl leading-none">{c.name}</p>
                        <p className="mt-1 text-[13px] font-medium text-sand/85">{c.school}</p>
                      </div>
                      <span className="rounded-full bg-sand px-2.5 py-1 text-[13px] font-bold text-ink">
                        {pct}%
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-1 flex-col p-6">
                    <p className="text-base leading-relaxed text-olive line-clamp-3">{c.story}</p>

                    <div className="mt-5">
                      <div className="flex items-baseline justify-between text-[13px]">
                        <span className="text-olive">Still needed</span>
                        <span className="font-bold text-ink">{money(remaining)}</span>
                      </div>
                      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-secondary">
                        <motion.div
                          className="h-full rounded-full bg-grass"
                          initial={{ width: 0 }}
                          whileInView={{ width: `${pct}%` }}
                          viewport={{ once: true, margin: "-10%" }}
                          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        />
                      </div>
                    </div>

                    <div className="mt-5 flex items-center justify-between gap-2 rounded-full bg-ink px-4 py-2.5 text-sand transition-colors group-hover:bg-grass">
                      <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold">
                        <FireIcon className="h-3.5 w-3.5 text-yellow" />
                        Chip in before time runs out
                      </span>
                      <ArrowUpRight className="h-4 w-4 text-sand transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2} />
                    </div>
                  </div>
                </Link>
              </FadeUp>
            );
          })}
        </div>
      </div>
    </section>
  );
};
